import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";

// Types
export interface AggregateForTest {
  id: number;
  materialCode: string;
  name: string;
  category: string;
  currentMoisture: number; // Current calibrated moisture %
  lastTestDate: string | null;
  storageLocation: string;
}

export interface MoistureTestData {
  testId: string;
  materialId: number | null;
  sampleWeight: number; // Wet weight in grams
  driedWeight: number; // Dry weight in grams (after cookoff)
  moisturePercent: number; // Calculated
  testDate: string;
  testTime: string;
  testedBy: string;
  notes: string;
  ovenTemp: number; // Temperature in Fahrenheit
  dryingTime: number; // Minutes
}

export interface MoistureTestRecord {
  id: number;
  testId: string;
  materialCode: string;
  materialName: string;
  sampleWeight: number;
  driedWeight: number;
  moisturePercent: number;
  testDate: string;
  testTime: string;
  testedBy: string;
  status: 'completed' | 'pending_review';
}

// Mock aggregates that can be tested
const MOCK_AGGREGATES: AggregateForTest[] = [
  { id: 1, materialCode: 'AGG-SAND', name: 'Sand (Fine Aggregate)', category: 'aggregate', currentMoisture: 4.2, lastTestDate: '2024-12-09', storageLocation: 'Yard Pile A' },
  { id: 2, materialCode: 'AGG-GRAVEL', name: 'Gravel (Coarse Aggregate)', category: 'aggregate', currentMoisture: 1.8, lastTestDate: '2024-12-08', storageLocation: 'Yard Pile B' },
  { id: 3, materialCode: 'AGG-STONE', name: '3/4" Crushed Stone', category: 'aggregate', currentMoisture: 0.9, lastTestDate: '2024-12-07', storageLocation: 'Yard Pile C' },
  { id: 4, materialCode: 'AGG-PEA', name: 'Pea Gravel', category: 'aggregate', currentMoisture: 2.1, lastTestDate: '2024-12-06', storageLocation: 'Yard Pile D' },
];

// Mock test history
const MOCK_TEST_HISTORY: MoistureTestRecord[] = [
  { id: 1, testId: 'MCT-241209-001', materialCode: 'AGG-SAND', materialName: 'Sand (Fine Aggregate)', sampleWeight: 500, driedWeight: 479, moisturePercent: 4.38, testDate: '2024-12-09', testTime: '06:30', testedBy: 'Mike Wilson', status: 'completed' },
  { id: 2, testId: 'MCT-241209-002', materialCode: 'AGG-GRAVEL', materialName: 'Gravel (Coarse Aggregate)', sampleWeight: 500, driedWeight: 491, moisturePercent: 1.83, testDate: '2024-12-09', testTime: '06:45', testedBy: 'Mike Wilson', status: 'completed' },
  { id: 3, testId: 'MCT-241208-001', materialCode: 'AGG-SAND', materialName: 'Sand (Fine Aggregate)', sampleWeight: 500, driedWeight: 481, moisturePercent: 3.95, testDate: '2024-12-08', testTime: '06:15', testedBy: 'Tom Davis', status: 'completed' },
  { id: 4, testId: 'MCT-241208-002', materialCode: 'AGG-STONE', materialName: '3/4" Crushed Stone', sampleWeight: 500, driedWeight: 495, moisturePercent: 1.01, testDate: '2024-12-08', testTime: '06:30', testedBy: 'Tom Davis', status: 'completed' },
  { id: 5, testId: 'MCT-241207-001', materialCode: 'AGG-SAND', materialName: 'Sand (Fine Aggregate)', sampleWeight: 500, driedWeight: 478, moisturePercent: 4.60, testDate: '2024-12-07', testTime: '05:45', testedBy: 'Mike Wilson', status: 'completed' },
];

class QCInspectionWorkflowStore {
  rootStore: RootStore;

  // Mock Data
  aggregates: AggregateForTest[] = [...MOCK_AGGREGATES];
  testHistory: MoistureTestRecord[] = [...MOCK_TEST_HISTORY];

  // UI State
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  // Test Data
  testData: MoistureTestData = {
    testId: '',
    materialId: null,
    sampleWeight: 500, // Default 500g sample
    driedWeight: 0,
    moisturePercent: 0,
    testDate: new Date().toISOString().split('T')[0],
    testTime: new Date().toTimeString().slice(0, 5),
    testedBy: 'Current User',
    notes: '',
    ovenTemp: 230, // Standard temp for sand moisture test
    dryingTime: 15, // Standard 15 minutes
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.testData.testId = this.generateTestId();
    makeAutoObservable(this, { rootStore: false });
  }

  generateTestId = (): string => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const seq = (this.testHistory.length + 1).toString().padStart(3, '0');
    return `MCT-${year}${month}${day}-${seq}`;
  };

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    this.activeStep++;
  };

  prevStep = () => {
    this.activeStep--;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  selectMaterial = (materialId: number | null) => {
    this.testData.materialId = materialId;
  };

  updateTestData = <K extends keyof MoistureTestData>(field: K, value: MoistureTestData[K]) => {
    this.testData[field] = value;

    // Auto-calculate moisture when weights are updated
    if (field === 'sampleWeight' || field === 'driedWeight') {
      this.calculateMoisture();
    }
  };

  calculateMoisture = () => {
    const { sampleWeight, driedWeight } = this.testData;
    if (sampleWeight > 0 && driedWeight > 0 && sampleWeight > driedWeight) {
      // Moisture % = ((Wet - Dry) / Dry) * 100
      const moisture = ((sampleWeight - driedWeight) / driedWeight) * 100;
      this.testData.moisturePercent = Math.round(moisture * 100) / 100;
    } else {
      this.testData.moisturePercent = 0;
    }
  };

  submitTest = async () => {
    this.loading = true;
    this.error = null;

    try {
      await new Promise(resolve => setTimeout(resolve, 500));

      runInAction(() => {
        const material = this.selectedMaterial;

        // Add to test history
        const newRecord: MoistureTestRecord = {
          id: this.testHistory.length + 1,
          testId: this.testData.testId,
          materialCode: material?.materialCode || '',
          materialName: material?.name || '',
          sampleWeight: this.testData.sampleWeight,
          driedWeight: this.testData.driedWeight,
          moisturePercent: this.testData.moisturePercent,
          testDate: this.testData.testDate,
          testTime: this.testData.testTime,
          testedBy: this.testData.testedBy,
          status: 'completed',
        };
        this.testHistory.unshift(newRecord);

        // Update material's current moisture
        if (material) {
          material.currentMoisture = this.testData.moisturePercent;
          material.lastTestDate = this.testData.testDate;
        }

        this.success = `Moisture test ${this.testData.testId} recorded successfully! ${material?.name} moisture updated to ${this.testData.moisturePercent}%.`;
        this.loading = false;
        this.nextStep();
      });
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || 'Failed to submit test';
        this.loading = false;
      });
    }
  };

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.testData = {
      testId: this.generateTestId(),
      materialId: null,
      sampleWeight: 500,
      driedWeight: 0,
      moisturePercent: 0,
      testDate: new Date().toISOString().split('T')[0],
      testTime: new Date().toTimeString().slice(0, 5),
      testedBy: 'Current User',
      notes: '',
      ovenTemp: 230,
      dryingTime: 15,
    };
  };

  // Computed: Selected material
  get selectedMaterial(): AggregateForTest | null {
    if (!this.testData.materialId) return null;
    return this.aggregates.find(a => a.id === this.testData.materialId) || null;
  }

  // Computed: Moisture difference from previous
  get moistureDifference(): number | null {
    const material = this.selectedMaterial;
    if (!material) return null;
    return Math.round((this.testData.moisturePercent - material.currentMoisture) * 100) / 100;
  }

  // Computed: Is moisture in normal range (0-8% for sand)
  get moistureStatus(): 'normal' | 'high' | 'low' {
    const moisture = this.testData.moisturePercent;
    if (moisture < 1) return 'low';
    if (moisture > 7) return 'high';
    return 'normal';
  }

  // Validation
  get canProceedFromStep1(): boolean {
    return !!this.testData.materialId;
  }

  get canProceedFromStep2(): boolean {
    return (
      this.testData.sampleWeight > 0 &&
      this.testData.driedWeight > 0 &&
      this.testData.driedWeight < this.testData.sampleWeight &&
      this.testData.moisturePercent > 0
    );
  }

  // Stats
  get testStats() {
    const today = new Date().toISOString().split('T')[0];
    const todaysTests = this.testHistory.filter(t => t.testDate === today);

    return {
      totalTests: this.testHistory.length,
      todaysTests: todaysTests.length,
      avgMoisture: this.testHistory.length > 0
        ? Math.round((this.testHistory.reduce((sum, t) => sum + t.moisturePercent, 0) / this.testHistory.length) * 100) / 100
        : 0,
    };
  }

  // Tests by material
  get testsByMaterial() {
    const grouped: Record<string, MoistureTestRecord[]> = {};
    this.testHistory.forEach(test => {
      if (!grouped[test.materialCode]) {
        grouped[test.materialCode] = [];
      }
      grouped[test.materialCode].push(test);
    });
    return grouped;
  }
}

export default QCInspectionWorkflowStore;
